import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Fade,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { SearchProvider, useSearch } from '../context/SearchContext';
import EnhancedSearchInterface from '../components/search/EnhancedSearchInterface';
import ResponsiveFilterSidebar from '../components/search/ResponsiveFilterSidebar';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

/**
 * Composant interne qui utilise le contexte de recherche
 */
const SearchPageContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const {
    isLoading,
    error,
    clearError,
    performSearch,
    updateQuery,
    updateFilters,
    updateSearchType,
    isSearchActive,
    hasResults
  } = useSearch();

  const [showWelcome, setShowWelcome] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [notification, setNotification] = useState(null);

  // 📢 GESTION DES NOTIFICATIONS
  const handleNotification = (notificationData) => {
    setNotification(notificationData);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // 🔄 GESTION DES CHANGEMENTS DE FILTRES (MISE À JOUR UNIQUEMENT)
  const handleFiltersChange = (filters) => {
    console.log('🔄 [SearchPage] Mise à jour des filtres (sans recherche automatique):', filters);
    updateFilters(filters);
    
    // Déclencher la recherche uniquement si appelé depuis le bouton "Appliquer"
    if (filters._triggerSearch) {
      console.log('🔍 [SearchPage] Déclenchement de la recherche depuis le bouton Appliquer');
      performSearch({ filters, forceRefresh: true });
    }
  };

  // Initialisation depuis les paramètres URL
  useEffect(() => {
    // Éviter de ré-exécuter l'initialisation (scroll + recherche) quand
    // performSearch change de référence (ex: pendant un loadMore)
    if (isInitialized) return;

    // Toujours remonter en haut de la page à l'arrivée depuis une redirection
    window.scrollTo({ top: 0, behavior: 'instant' });

    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get('q') || '';
    const category = searchParams.get('category');
    const city = searchParams.get('city');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    // 'type' peut valoir 'listings', 'products' ou 'all'
    const typeParam = searchParams.get('type');

    // Appliquer le type de recherche si fourni
    if (typeParam && ['listings', 'products', 'all'].includes(typeParam)) {
      updateSearchType(typeParam);
    }

    // Appliquer les paramètres URL
    if (query) {
      updateQuery(query);
    }

    const filters = {};
    if (category) filters.categoryId = category;
    if (city) filters.city = city;
    if (minPrice) filters.minPrice = parseFloat(minPrice);
    if (maxPrice) filters.maxPrice = parseFloat(maxPrice);

    if (Object.keys(filters).length > 0) {
      updateFilters(filters);
    }

    // Effectuer une recherche initiale si des paramètres sont présents
    if (query || Object.keys(filters).length > 0 || typeParam) {
      performSearch({ query, searchType: typeParam || 'all', forceRefresh: true });
    } else if (!user) {
      // Afficher un message de bienvenue pour les utilisateurs non connectés
      setShowWelcome(true);
    }

    setIsInitialized(true);
  }, [location.search, updateQuery, updateFilters, updateSearchType, performSearch, user]);

  // Mettre à jour l'URL quand la recherche change
  useEffect(() => {
    if (!isInitialized) return;

    const searchParams = new URLSearchParams();
    
    // Ajouter les paramètres de recherche à l'URL
    const query = location.state?.query;
    if (query) searchParams.set('q', query);
    
    // Construire la nouvelle URL
    const newSearch = searchParams.toString();
    const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    
    // Mettre à jour l'URL sans recharger la page
    if (newPath !== `${location.pathname}${location.search}`) {
      navigate(newPath, { replace: true });
    }
  }, [location.state, navigate, location.pathname, location.search, isInitialized]);

  // Gestion des erreurs
  const handleCloseError = () => {
    clearError();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' ? '#111' : '#f7f7f7',
        display: 'flex',
        flexDirection: 'column',
        pb: 8  // Marge inférieure pour éviter le chevauchement avec le footer
      }}
    >
      {/* Interface de recherche intégrée avec accordéons */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {showWelcome && !isSearchActive && (
          <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
            <Fade in timeout={1000}>
              <Box>
                <Box
                  component="img"
                  src="/assets/search-welcome.svg"
                  alt="Recherche"
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 'auto',
                    mb: 4,
                    opacity: 0.8
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Box
                  sx={{
                    typography: 'h3',
                    fontWeight: 700,
                    mb: 2,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {t('search.welcome.title')}
                </Box>
                <Box sx={{ typography: 'h6', color: 'text.secondary', mb: 4 }}>
                  {t('search.welcome.subtitle')}
                </Box>
                <Box sx={{ typography: 'body1', color: 'text.secondary' }}>
                  {t('search.welcome.description')}
                </Box>
              </Box>
            </Fade>
          </Container>
        )}

        {/* Interface de recherche améliorée - toujours visible */}
        <EnhancedSearchInterface />

        {/* Sidebar de filtres avancés avec design et animations */}
        <ResponsiveFilterSidebar 
          onFiltersChange={handleFiltersChange}
          onNotification={handleNotification}
        />
      </Box>

      {/* Backdrop de chargement */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
        open={isLoading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress 
            color="primary" 
            size={60}
            thickness={4}
            sx={{
              mb: 2,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
          />
          <Box sx={{ typography: 'h6', fontWeight: 500 }}>
            {t('search.loading.searching')}
          </Box>
          <Box sx={{ typography: 'body2', opacity: 0.8, mt: 1 }}>
            {t('search.loading.subtitle')}
          </Box>
        </Box>
      </Backdrop>

      {/* Snackbar d'erreur */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[8]
          }}
        >
          {error?.message || t('search.errors.general')}
        </Alert>
      </Snackbar>

      {/* Snackbar de notifications du sidebar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {notification && (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: theme.shadows[8]
            }}
          >
            <Box>
              <Box sx={{ fontWeight: 'bold' }}>{notification.title}</Box>
              {notification.message && (
                <Box sx={{ fontSize: '0.875rem', mt: 0.5 }}>{notification.message}</Box>
              )}
            </Box>
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

/**
 * Page de recherche principale avec provider
 */
const SearchPage = () => {
  return (
    <SearchProvider>
      <SearchPageContent />
    </SearchProvider>
  );
};

export default SearchPage;